import { AsyncPipe, DatePipe, DOCUMENT, NgClass, NgFor, NgIf, SlicePipe } from "@angular/common";
import { Component, Signal, ViewEncapsulation, inject, OnInit } from "@angular/core";

import { Observable, shareReplay, startWith } from "rxjs";
import { map } from "rxjs/operators";

import { Actions, Store } from "@ngxs/store";

import { MenuItem } from "primeng/api";
import { ButtonModule } from "primeng/button";
import { MenuModule } from "primeng/menu";
import { SkeletonModule } from "primeng/skeleton";

import { Nullable } from "@angular-ru/cdk/typings";

import { SubscriberClass } from "@core/classes/subscriber.class";
import { SUPPORT_CONTACT_USER_IDS } from "@core/constants/core.constants";
import { hasActionsRunning } from "@core/helpers/handlers.helper";
import { Nulled } from "@core/interfaces/core.interface";
import { IRouteStatus } from "@core/interfaces/route-status.interface";
import { ScreenWidthDetectorService } from "@core/services/screen-width-detector.service";
import { GlobalActions } from "@core/store/core.actions";
import { CoreStateSelector } from "@core/store/core.selectors";

import { getMenuModel } from "./enums/menu.enum";

@Component({
    standalone: true,
    selector: "ruts-menu",
    templateUrl: "./menu.component.html",
    styleUrls: ["./menu.component.scss"],
    encapsulation: ViewEncapsulation.None,
    imports: [
        NgIf,
        NgFor,
        AsyncPipe,
        DatePipe,
        SlicePipe,

        // PrimeNG
        ButtonModule,
        MenuModule,
        SkeletonModule,
    ],
})
export class MenuComponent extends SubscriberClass implements OnInit {
    readonly #store: Store = inject(Store);
    readonly #actions$: Actions = inject(Actions);
    readonly #swd: ScreenWidthDetectorService = inject(ScreenWidthDetectorService);
    readonly #document: Document = inject<Document>(DOCUMENT);

    protected readonly supportListIds: ReadonlySet<number> = SUPPORT_CONTACT_USER_IDS;

    public readonly swdIsDesktop: Signal<Nullable<boolean>> = this.#swd.isDesktop;

    readonly menuStatus$: Observable<Nulled<IRouteStatus>> = this.#store.select(
        CoreStateSelector.getMenuStatus
    );
    readonly isLoading$ = hasActionsRunning(this.#actions$, [GlobalActions.AppInit]);
    readonly user$ = this.#store.select(CoreStateSelector.getUser);

    menuItems$: Observable<MenuItem[]> = this.user$.pipe(
        map(currentUser =>
            getMenuModel({
                isAdmin: currentUser?.isAdmin,
                partnersAvailable: currentUser?.partnersAvailable,
                isNewbie: currentUser?.isNewbie,
                contact: currentUser?.contact,
            })
        ),
        startWith(getMenuModel({})),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    constructor() {
        super();
    }

    ngOnInit() {
        this.addSub([
            this.menuStatus$.subscribe(item => {
                const menuItem =
                    this.#document.querySelector(".p-menuitem-icon.breach")?.parentElement;

                if (item?.breach) {
                    return menuItem?.classList.add("has-unread");
                }

                return menuItem?.classList.remove("has-unread");
            }),
        ]);
    }

    closeMenu() {
        const menu = this.#document.querySelector(".ruts-menu");

        menu?.classList.remove("active");
    }
}
